export type BrokerId = 'fyers' | 'zerodha' | 'dhanhq' | 'mosl' | 'nubra';

export type StrategyName = 'Optimus' | 'Polaris' | 'Polaris Lite' | 'Pledge+' | 'Pledge+ Mini';

export const STRATEGY_ID_TO_NAME: Record<string, StrategyName> = {
  optimus: 'Optimus',
  pledge_plus: 'Pledge+',
  pledge_plus_mini: 'Pledge+ Mini',
  polaris: 'Polaris Lite',
  polaris_lite: 'Polaris Lite',
};

/** Must stay in sync with optimus_backend/config/strategy_brokers.py */
export const STRATEGY_BROKERS: Record<StrategyName, BrokerId[]> = {
  Optimus: ['fyers', 'dhanhq'],
  'Pledge+': ['fyers', 'dhanhq', 'zerodha'],
  'Pledge+ Mini': ['fyers', 'dhanhq', 'zerodha'],
  Polaris: ['fyers', 'dhanhq', 'zerodha'],
  'Polaris Lite': ['fyers', 'dhanhq', 'zerodha'],
};

export const BROKER_ORDER: BrokerId[] = ['fyers', 'zerodha', 'dhanhq', 'mosl', 'nubra'];

export interface BrokerMeta {
  id: BrokerId;
  name: string;
  /** Two-letter initials shown in broker picker UI */
  initials: string;
  /** @deprecated Use initials — kept for legacy references */
  logo: string;
  description: string;
  registrationUrl: string;
  registrationSteps: string[];
}

export function getBrokerInitials(brokerId: BrokerId): string {
  return BROKER_META[brokerId]?.initials ?? brokerId.slice(0, 2).toUpperCase();
}

export const BROKER_META: Record<BrokerId, BrokerMeta> = {
  fyers: {
    id: 'fyers',
    name: 'Fyers',
    initials: 'FY',
    logo: 'FY',
    description: 'Advanced trading platform with powerful analytics.',
    registrationUrl: 'https://signup.fyers.in/',
    registrationSteps: [
      'Open a Fyers trading account',
      'Create an API app in Fyers developer dashboard',
      'Note your Client ID, App ID, App Secret, TOTP key, and PIN',
      'Return here to connect your account',
    ],
  },
  zerodha: {
    id: 'zerodha',
    name: 'Zerodha',
    initials: 'ZE',
    logo: 'ZE',
    description: "India's largest discount broker.",
    registrationUrl: 'https://zerodha.com/open-account',
    registrationSteps: [
      'Open a Zerodha account',
      'Generate API key from Kite Connect developer console',
      'Obtain session / request token for API access',
      'Connect using Client ID and API credentials',
    ],
  },
  dhanhq: {
    id: 'dhanhq',
    name: 'DhanHQ',
    initials: 'DH',
    logo: 'DH',
    description: 'Modern API-first broker platform.',
    registrationUrl: 'https://login.dhan.co/',
    registrationSteps: [
      'Open a Dhan trading account',
      'Generate access token from Dhan developer portal',
      'Note your Client ID and Access Token',
      'Connect here to verify your account',
    ],
  },
  mosl: {
    id: 'mosl',
    name: 'MOSL',
    initials: 'MO',
    logo: 'MO',
    description: 'Motilal Oswal Financial Services.',
    registrationUrl: 'https://www.motilaloswal.com/',
    registrationSteps: [
      'Open a Motilal Oswal trading account',
      'Generate API credentials',
      'Connect here to verify your account',
    ],
  },
  nubra: {
    id: 'nubra',
    name: 'Nubra',
    initials: 'NU',
    logo: 'NU',
    description: 'Nubra trading platform.',
    registrationUrl: 'https://nubra.in/',
    registrationSteps: [
      'Open a Nubra trading account',
      'Generate API key and TOTP key',
      'Connect here to verify your account',
    ],
  },
};

export function brokersForStrategies(strategies: StrategyName[]): BrokerId[] {
  const seen = new Set<BrokerId>();
  for (const s of strategies) {
    for (const b of STRATEGY_BROKERS[s] || []) {
      seen.add(b);
    }
  }
  return BROKER_ORDER.filter((b) => seen.has(b));
}

export function strategyNameFromId(id: string): StrategyName | null {
  return STRATEGY_ID_TO_NAME[id.trim()] ?? null;
}

export function strategyNamesFromIds(raw: string): StrategyName[] {
  if (!raw) return [];
  return [
    ...new Set(
      raw
        .split(',')
        .map((id) => strategyNameFromId(id))
        .filter((n): n is StrategyName => n != null)
    ),
  ];
}

export function resolveStrategyNamesFromStorage(): StrategyName[] {
  const byName = localStorage.getItem('selected_strategy_name');
  if (byName) {
    return normalizeStrategyNames([byName]);
  }
  return strategyNamesFromIds(localStorage.getItem('selected_strategy_id') || '');
}

/** Resolve display names from API/local id strings (e.g. "Optimus" or "optimus"). */
export function normalizeStrategyNames(input: string[]): StrategyName[] {
  const out: StrategyName[] = [];
  for (const item of input) {
    const trimmed = item.trim();
    if (!trimmed) continue;
    if (trimmed === 'Polaris' || trimmed === 'Polaris Lite' || trimmed === 'polaris' || trimmed === 'polaris_lite') {
      out.push('Polaris Lite');
      continue;
    }
    if (trimmed in STRATEGY_BROKERS) {
      out.push(trimmed as StrategyName);
      continue;
    }
    const fromId = strategyNameFromId(trimmed.toLowerCase().replace(/\s+/g, '_'));
    if (fromId) out.push(fromId);
  }
  return [...new Set(out)];
}

/**
 * Query param for `/api/v1/portfolio/*`.
 * Optimus uses the default `portfolio` cache (no param).
 * Pledge+ must use `pledge_plus` — never `pledge+` (+ decodes as space in URLs).
 * Polaris uses `polaris` (its own bucket); it has no cache table yet, so the
 * backend returns an empty portfolio rather than Optimus data.
 */
export function portfolioStrategyQueryParam(
  strategy: StrategyName | string | null | undefined
): string | undefined {
  if (!strategy) return undefined;
  if (strategy === 'Pledge+') return 'pledge_plus';
  if (strategy === 'Polaris' || strategy === 'Polaris Lite') return 'polaris';

  const compact = String(strategy).trim().toLowerCase().replace(/[\s_+]/g, '');
  if (compact === 'pledgeplus' || compact === 'pledge') {
    return 'pledge_plus';
  }
  if (String(strategy).trim().toLowerCase() === 'pledge_plus') {
    return 'pledge_plus';
  }
  if (compact === 'polaris' || compact === 'polarislite') {
    return 'polaris';
  }
  return undefined;
}

export function normalizeBrokerId(broker: string | null | undefined): BrokerId | null {
  if (!broker) return null;
  const b = String(broker).toLowerCase().trim().replace(/[^a-z0-9]/g, '');
  if (b === 'fyers') return 'fyers';
  if (b === 'zerodha') return 'zerodha';
  if (b === 'dhan' || b === 'dhanhq') return 'dhanhq';
  if (b === 'mosl' || b === 'motilal' || b === 'motilaloswal') return 'mosl';
  if (b === 'nubra') return 'nubra';
  return null;
}
